/**
 * Copyright (c) 2025 Stefan Radig
 *
 * One-time local storage reset.
 *
 * The local storage formats changed (v2: encrypted per-document stores keyed
 * by file ID, encrypted file cache, file-ID-keyed localStorage; v3: the
 * editoor-local:<fileId> document store and IndexedDB outbox, replacing the
 * editoor-doc:<fileId> store and the localStorage outbox; v4: the same
 * stores again, after the sync-core simplification changed what they hold,
 * for devices that already ran a v3 build; v5: the device cache stores
 * binary rows (encryptBinary of each decrypted update, no longer the
 * server's base64 strings, which the new code cannot read), and cached
 * documents get a localStorage marker (cached:<fileId>) that must start
 * out in step with the databases). Rather than
 * migrate, every device wipes all local data the first time this version
 * starts, as if freshly installed, and re-downloads from the server.
 *
 * Wiped: every IndexedDB database, localStorage, sessionStorage.
 * Kept: service-worker caches and Workbox's IndexedDB databases (app shell,
 * not user data).
 * Needs no network access.
 *
 * Bump STORAGE_VERSION to run another reset; a database left behind by a
 * blocked delete is then removed by the next reset.
 *
 * At every later startup a cheap sweep (sweepLegacyStorage) removes the
 * legacy per-document localStorage keys a tab still running old code
 * recreates after the wipe. It never lists the IndexedDB databases: in
 * WebKit indexedDB.databases() holds the origin's storage while it runs
 * (2.4 s for 2,857 databases on a Mac), and the service worker's cache
 * lookups wait behind it, so the first page after launch did too. A
 * recreated legacy database is removed by the next reset.
 */

import { DebugLogger } from './debugLogger';

export const STORAGE_VERSION = 6;
export const STORAGE_VERSION_KEY = 'editoor:storageVersion';
export const STORAGE_RESET_LOCK = 'editoor-storage-reset';

/** How long to wait for one database delete before giving up on it. */
export const DELETE_TIMEOUT_MS = 5000;

/** Old y-indexeddb databases were named by the 32 lowercase hex document ID. */
const OLD_DOCUMENT_DB_NAME = /^[0-9a-f]{32}$/;

/** The per-document localStorage keys of the v2 sync (docStorage docKey: `<kind>:<fileId>`). */
const LEGACY_KEY_PREFIXES = ['pendingUpdates:', 'lastUpdateId:', 'cacheOnly:', 'staleCache:'];

/** Exact keys of removed features (last drive/document, breadcrumb path, drag data) an old-code tab could recreate. */
const LEGACY_EXACT_KEYS = ['editoor-last-drive', 'editoor:lastDocument', 'editoor-breadcrumb-path', 'editoor-drag-data'];

/** Service-worker (Workbox) databases hold the app shell, not user data. */
const SERVICE_WORKER_DB_PREFIX = 'workbox-';

/** Plugin entries derived from a document ID: namespace is the plugin type. */
const KEY_PREFIX_LENGTH = 16;

export interface SecureKeyRef {
    namespace: string;
    key: string;
}

export interface StorageResetOptions {
    /**
     * Best-effort removal of secure-storage entries (vault/gate remembered
     * passwords) derived from old document IDs. Only the shell can provide
     * this. Failures are logged and never block the reset.
     */
    deleteSecureKeys?: (entries: SecureKeyRef[]) => Promise<void>;
    /** Override the per-database delete timeout (tests). */
    deleteTimeoutMs?: number;
}

/** Secure-storage entries the vault and gate plugins derive from a document ID. */
export function secureKeysForDocument(documentId: string): SecureKeyRef[] {
    const short = documentId.substring(0, KEY_PREFIX_LENGTH);
    return [
        { namespace: 'vault', key: `password-${short}` },
        { namespace: 'vault', key: `vault-password-${short}` },
        { namespace: 'gate', key: `gate-password-${short}` },
    ];
}

function markerIsCurrent(): boolean {
    try {
        const stored = Number(localStorage.getItem(STORAGE_VERSION_KEY));
        return Number.isFinite(stored) && stored >= STORAGE_VERSION;
    } catch {
        return false;
    }
}

async function listDatabaseNames(timeoutMs: number): Promise<string[]> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        const timeout = new Promise<null>(resolve => {
            timer = setTimeout(() => resolve(null), timeoutMs);
        });
        const databases = await Promise.race([indexedDB.databases(), timeout]);
        if (databases === null) {
            DebugLogger.log('error', 'StorageReset: indexedDB.databases() timed out, continuing with no databases', { timeoutMs });
            return [];
        }
        return databases
            .map(d => d.name)
            .filter((n): n is string => !!n && !n.startsWith(SERVICE_WORKER_DB_PREFIX));
    } catch (error) {
        DebugLogger.log('error', 'StorageReset: cannot enumerate IndexedDB databases', { error: String(error) });
        return [];
    } finally {
        clearTimeout(timer);
    }
}

function deleteDatabase(name: string, timeoutMs: number): Promise<void> {
    return new Promise<void>(resolve => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(() => {
            DebugLogger.log('error', 'StorageReset: database delete still blocked, continuing', { timeoutMs });
            finish();
        }, timeoutMs);
        try {
            const request = indexedDB.deleteDatabase(name);
            request.onsuccess = finish;
            request.onerror = () => {
                DebugLogger.log('error', 'StorageReset: database delete failed', { error: String(request.error) });
                finish();
            };
            request.onblocked = () => {
                DebugLogger.log('storage', 'StorageReset: database delete blocked by another connection');
            };
        } catch (error) {
            DebugLogger.log('error', 'StorageReset: database delete threw', { error: String(error) });
            finish();
        }
    });
}

async function performReset(options: StorageResetOptions): Promise<boolean> {
    // Re-check inside the lock: another tab may have finished while we waited.
    if (markerIsCurrent()) return false;

    DebugLogger.log('storage', 'StorageReset: wiping local storage for new storage format', { version: STORAGE_VERSION });

    const timeoutMs = options.deleteTimeoutMs ?? DELETE_TIMEOUT_MS;
    const names = await listDatabaseNames(timeoutMs);

    // Collect old document IDs before the databases disappear.
    if (options.deleteSecureKeys) {
        const entries = names
            .filter(n => OLD_DOCUMENT_DB_NAME.test(n))
            .flatMap(secureKeysForDocument);
        if (entries.length > 0) {
            try {
                await options.deleteSecureKeys(entries);
            } catch (error) {
                DebugLogger.log('error', 'StorageReset: secure storage cleanup failed', { error: String(error) });
            }
        }
    }

    await Promise.all(names.map(name => deleteDatabase(name, timeoutMs)));

    try { localStorage.clear(); } catch (error) {
        DebugLogger.log('error', 'StorageReset: localStorage.clear failed', { error: String(error) });
    }
    try { sessionStorage.clear(); } catch (error) {
        DebugLogger.log('error', 'StorageReset: sessionStorage.clear failed', { error: String(error) });
    }

    try {
        localStorage.setItem(STORAGE_VERSION_KEY, String(STORAGE_VERSION));
    } catch (error) {
        DebugLogger.log('error', 'StorageReset: cannot set marker', { error: String(error) });
    }
    return true;
}

/**
 * Removes the legacy per-document localStorage keys (and keys of removed
 * features) that a tab still running old code recreated after the wipe:
 * one key scan, no IndexedDB access (see the module comment). Never throws.
 */
export function sweepLegacyStorage(): void {
    try {
        const legacyKeys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (LEGACY_EXACT_KEYS.includes(key) || LEGACY_KEY_PREFIXES.some(prefix => key.startsWith(prefix)))) legacyKeys.push(key);
        }
        for (const key of legacyKeys) localStorage.removeItem(key);
        if (legacyKeys.length > 0) DebugLogger.log('storage', 'StorageReset: removed legacy localStorage keys', { count: legacyKeys.length });
    } catch (error) {
        DebugLogger.log('error', 'StorageReset: legacy localStorage sweep failed', { error: String(error) });
    }
}

/**
 * Wipe all local data once per STORAGE_VERSION.
 *
 * Must run before anything opens storage. Serialized across tabs and frames
 * with a Web Lock so concurrent starts wipe once. When no wipe is needed it
 * runs sweepLegacyStorage (a localStorage key scan).
 *
 * @returns true if this call wiped the device.
 */
export async function runStorageResetIfNeeded(options: StorageResetOptions = {}): Promise<boolean> {
    if (markerIsCurrent()) {
        sweepLegacyStorage();
        return false;
    }

    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks) {
        return performReset(options);
    }
    return locks.request(STORAGE_RESET_LOCK, () => performReset(options));
}
